import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Pagination, Table } from "antd";
import { useSelector } from "react-redux";
import { TrainingAPI } from "@api/TrainingAPI";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import "@styles/styles-ResultsTable.css";

export function TestingForceplateMetricTable({
  metricType,
  metricIdentifiers = [],
  positionFilter,
  playerId,
}: Props) {
  const { forceplateStatDefaults, testingStatDefaults, filter: teamFilter, info: { display_name: teamDisplayName } } = useSelector(({ team }: { team: TeamState }) => team);
  const playerFilter = useSelector(({ player }: { player: PlayerState }) => player.filter);

  const isPlayerView = playerId != null;

  const { player_group, session, startDate, endDate } = isPlayerView
    ? { player_group: undefined, session: playerFilter.session, startDate: playerFilter.startDate, endDate: playerFilter.endDate }
    : { player_group: teamFilter.player_group, session: teamFilter.session, startDate: teamFilter.startDate, endDate: teamFilter.endDate };

  const statDefaults =
    metricType === "forceplate" ? forceplateStatDefaults : testingStatDefaults;

  const effectiveMetrics = useMemo(() => {
    const fromProp = metricIdentifiers.filter(Boolean);
    return fromProp.length > 0 ? fromProp : statDefaults.filter(Boolean);
  }, [metricIdentifiers, statDefaults]);

  const [tableData, setTableData] = useState<TableData>({ metrics: [], data: [] });
  const [loading, setLoading] = useState(false);
  const [showBest, setShowBest] = useState(true);
  const [showPctOfBest, setShowPctOfBest] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const trainingAPI = useMemo(() => new TrainingAPI(), []);

  useEffect(() => {
    if (!effectiveMetrics.length) return;
    if (teamDisplayName !== "San Francisco 49ers") return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = await (trainingAPI as any).getTestingDataTable(
          startDate,
          endDate,
          effectiveMetrics,
          positionFilter ?? undefined,
          player_group ?? undefined,
          session ?? undefined,
          playerId ?? undefined
        );
        setTableData(data);
        setPage(1);
      } catch {
        message.error("Error loading data table");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, effectiveMetrics.join(","), positionFilter, player_group, session, playerId, teamDisplayName]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (tableData.data || []).slice(start, start + pageSize).map((row, i) => ({ ...row, key: i }));
  }, [tableData.data, page, pageSize]);

  const columns = useMemo(() => {
    const dateCol = {
      title: "Date",
      dataIndex: "date",
      key: "date",
      fixed: "left" as const,
      width: 100,
      onHeaderCell: (): React.HTMLAttributes<HTMLElement> => ({
        style: { zIndex: 5 },
      }),
    };

    const metricCols = (tableData.metrics || []).map((metric) => {
      const children = [
        {
          title: "Value",
          key: `${metric.key}_value`,
          width: 80,
          onHeaderCell: (): React.HTMLAttributes<HTMLElement> => ({
            style: { borderLeft: "2px solid #d9d9d9" },
          }),
          onCell: (): React.HTMLAttributes<HTMLElement> => ({
            style: { borderLeft: "2px solid #d9d9d9" },
          }),
          render: (_: unknown, row: TableRow) => {
            const entry = row[metric.key] as MetricEntry | undefined;
            if (!entry || entry.value == null) return "";
            return entry.value.toFixed(metric.num_decimals);
          },
        },
        ...(showBest
          ? [
              {
                title: "ATH",
                key: `${metric.key}_best`,
                width: 80,
                render: (_: unknown, row: TableRow) => {
                  const entry = row[metric.key] as MetricEntry | undefined;
                  const val = entry?.best_all_time;
                  return val != null ? val.toFixed(metric.num_decimals) : "";
                },
              },
            ]
          : []),
        ...(showPctOfBest
          ? [
              {
                title: "% ATH",
                key: `${metric.key}_pct`,
                width: 80,
                render: (_: unknown, row: TableRow) => {
                  const entry = row[metric.key] as MetricEntry | undefined;
                  const pct = entry?.pct_of_best;
                  if (pct == null) return "";
                  const color =
                    pct >= 100 ? "#28a745" : pct < 85 ? "#ffc107" : "inherit";
                  return <span style={{ color, fontWeight: 600 }}>{pct.toFixed(2)}%</span>;
                },
              },
            ]
          : []),
      ];

      return {
        title: metric.name,
        onHeaderCell: (): React.HTMLAttributes<HTMLElement> => ({
          style: { borderLeft: "2px solid #d9d9d9" },
        }),
        children,
      };
    });

    return [dateCol, ...metricCols];
  }, [tableData.metrics, showBest, showPctOfBest]);

  if (teamDisplayName !== "San Francisco 49ers") return null;
  if (!effectiveMetrics.length) return null;

  return (
    <div style={{ width: "80%", margin: "0 auto", marginBottom: 50 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={tableData.data?.length ?? 0}
          pageSizeOptions={["10", "20", "50"]}
          showSizeChanger
          size="small"
          onChange={(p, ps) => { setPage(p); setPageSize(ps); }}
        />
        <div style={{ display: "flex", gap: 16 }}>
          <Checkbox checked={showBest} onChange={(e) => setShowBest(e.target.checked)}>
            Show ATH
          </Checkbox>
          <Checkbox checked={showPctOfBest} onChange={(e) => setShowPctOfBest(e.target.checked)}>
            Show % of ATH
          </Checkbox>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <Table
          columns={columns}
          dataSource={pagedData}
          loading={loading}
          size="small"
          scroll={{ x: "max-content" }}
          pagination={false}
          className="exportable tab-forceplate gpsdv-table team-table"
          style={{ marginBottom: 0 }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 16, marginTop: 8, fontSize: 12, color: "#666" }}>
        <span>% ATH</span>
        {([["#28a745", "≥ 100%"], ["#000000", "85–100%"], ["#ffc107", "< 85%"]] as const).map(([color, label]) => (
          <span key={label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color, display: "inline-block" }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

interface MetricInfo {
  key: string;
  name: string;
  num_decimals: number;
}

interface MetricEntry {
  value: number | null;
  best_all_time: number | null;
  pct_of_best: number | null;
}

interface TableRow {
  date: string;
  key?: number;
  [metricKey: string]: MetricEntry | string | number | undefined;
}

interface TableData {
  metrics: MetricInfo[];
  data: TableRow[];
}

interface TeamFilter {
  player_group: string | null;
  session: string | null;
  startDate: string | null;
  endDate: string | null;
}

interface TeamState {
  forceplateStatDefaults: string[];
  testingStatDefaults: string[];
  filter: TeamFilter;
  info: { display_name: string };
}

interface PlayerFilter {
  session: string | null;
  startDate: string | null;
  endDate: string | null;
}

interface PlayerState {
  filter: PlayerFilter;
}

interface Props {
  metricType: "forceplate" | "testing";
  metricIdentifiers?: string[];
  positionFilter?: string | null;
  playerId?: number | null;
}